import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" className="flex items-center">
            <img src="/logo-lockup.svg" alt="CartControl" className="h-7 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-400 mb-10">Last updated: April 2026</p>

        <div className="prose prose-slate prose-sm max-w-none space-y-8">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">What Data We Collect</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              CartControl collects the following data to provide our service:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li>Your Google account email address and profile name (via Google OAuth)</li>
              <li>Purchase receipt data from your Gmail inbox (order totals, merchant names, dates)</li>
              <li>Monthly budget preferences you set within the app</li>
              <li>Extension interaction data (warnings shown, cart abandonments)</li>
              <li>Feedback you voluntarily submit</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">How We Use Your Data</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Your data is used exclusively to provide CartControl's spending tracking and budget warning features. We calculate your monthly spending from parsed purchase receipts, compare it against your budget, and display warnings when you may be overspending.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Gmail Receipt Parsing</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              CartControl accesses your Gmail with read-only permissions to find and parse purchase receipt emails from supported retailers (such as Amazon). We only read emails that match specific receipt patterns. <strong>We do not read, store, or process personal emails, conversations, or any non-receipt content.</strong>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Third-Party Services</h2>
            <p className="text-sm text-gray-600 leading-relaxed">CartControl uses the following third-party services:</p>
            <ul className="list-disc list-inside text-sm text-gray-600 mt-2 space-y-1">
              <li><strong>Google OAuth & Gmail API</strong> — for authentication and receipt parsing</li>
              <li><strong>Supabase</strong> — for secure database storage</li>
              <li><strong>Stripe</strong> — for subscription payment processing</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Data Retention & Deletion</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              Your data is retained as long as your account is active. You may request account and data deletion at any time by contacting us at <a href="mailto:hello@cartcontrol.io" className="text-gray-900 underline">hello@cartcontrol.io</a>.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Contact</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              For privacy-related questions, contact us at <a href="mailto:hello@cartcontrol.io" className="text-gray-900 underline">hello@cartcontrol.io</a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
